/**
 * DatevExportPage — M7 Einstieg „Steuerberater-Export" (DATEV-Buchungsstapel).
 *
 * Lädt owner-scoped (M0) `invoices` (Muster OpenItemsPage) + `expenses`
 * (`getExpensesForUser`), mappt sie auf die entkoppelten `DatevInvoiceInput`/
 * `DatevExpenseInput`-Formen (inkl. Netto/USt-Rekonstruktion aus `rawData.items`
 * bzw. `rawData.calculatedTotals.taxSummary`, §3.1/§4.7) und reicht sie an den
 * reinen `DatevExportDialog`.
 *
 * Fail-closed: gerendert nur, wenn `canUseDatevExport` grünes Licht gibt
 * (Beta-Allowlist ODER allgemeines Flag — kein Premium-Term). Route ebenfalls
 * so gegated registriert (siehe `App.tsx`, `config/datevExportAccess.ts`).
 *
 * @module DatevExportPage
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconCsv, IconError } from '../icons';

import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';
import DatevExportDialog from '../components/datev/DatevExportDialog';
import {
  DatevInvoiceInput,
  DatevExpenseInput,
  DatevTravelInput,
  DatevExportResult,
  DatevHeaderConfig,
  buildBelegeCsvLines,
  buildDatevFilename,
} from '../utils/datevExport';
import { loadMandantDatevSource } from '../utils/datev/datevSource';
import { buildDatevBlob } from '../utils/datevCp1252';
import { downloadBlob } from '../api/invoiceApi';
import { SkrKontenrahmen } from '../utils/datevMapping';
import { canUseDatevExport, DATEV_EXPORT_FLAG } from '../config/datevExportAccess';
import { canUseDatevBelege } from '../config/datevBelegeAccess';
import { resolveTier } from '../config/entitlements';
import { canUseTravelExpenses } from '../config/travelExpensesAccess';
import { downloadDatevBelegePaket } from '../api/datevBelegeApi';
import { isLoopBBeta } from '../config/loopBBetaAccess';
import { useIsAdvisorContext } from '../hooks/useIsAdvisorContext';
import { shouldShowKanzleiUebergabePaywall } from '../utils/paywallTrigger4Suppression';
import PaywallModal from '../components/premium/PaywallModal';
import FeatureTeaser from '../components/premium/FeatureTeaser';
import AdvisorInviteExportSuccessCard from '../components/tenant/AdvisorInviteExportSuccessCard';
import { useAdvisorInviteFunnelStatus } from '../hooks/useAdvisorInviteFunnelStatus';
import {
  dismissAdvisorInviteCta,
  isAdvisorInviteCtaDismissed,
} from '../utils/advisorInviteCtaSuppression';

// ─── Teaser (PR-T6, D7) ────────────────────────────────────────────────────────

/**
 * PR-T6 (D7, §3.2): Upsell-Teaser für einen NICHT-berechtigten Owner bei
 * aktivem `DATEV_EXPORT_FLAG` (Ziel-Tier Ultra). NIE für einen Advisor
 * gerendert (s. `DatevExportPage`-Gate unten, T4b-Bypass respektiert).
 */
const DatevExportTeaser: React.FC = () => (
  <FeatureTeaser
    icon={<IconCsv size={30} aria-hidden />}
    badgeKey="datevExport.teaserBadge"
    badgeDefault="Ultra-Funktion"
    titleKey="datevExport.teaserTitle"
    titleDefault="DATEV-Export für Ihre Kanzlei"
    descriptionKey="datevExport.teaserDescription"
    descriptionDefault="Exportieren Sie Ihren Buchungsstapel inklusive Belegen direkt im DATEV-Format — für die reibungslose Übergabe an Ihre Steuerkanzlei."
    highlights={[
      { labelKey: 'datevExport.teaserHighlight1', defaultLabel: 'EXTF-Buchungsstapel auf Knopfdruck' },
      { labelKey: 'datevExport.teaserHighlight2', defaultLabel: 'Belege automatisch verknüpft' },
      {
        labelKey: 'datevExport.teaserHighlight3',
        defaultLabel: 'Direkt für DATEV Unternehmen online nutzbar',
      },
    ]}
    ctaKey="datevExport.teaserCta"
    ctaDefault="Jetzt auf Ultra upgraden"
    promotionId="feature_teaser_datev_export"
    creativeSlot="page"
  />
);

// ─── Page ────────────────────────────────────────────────────────────────────────

/**
 * Steuerberater-Export-Seite. Lädt Belege owner-scoped und öffnet den Export-Dialog.
 * Fail-closed: rendert nichts ohne aktives Feature-Flag.
 */
const DatevExportPage: React.FC = () => {
  const { t } = useTranslation('datev');
  // BUG-3: die Seite kennt den Router und navigiert für den F6-Deep-Link des
  // Dialogs (der Dialog selbst trägt keinen Router-Kontext mehr).
  const navigate = useNavigate();
  const { currentUser, effectiveScopeUid, sellerData, saveSellerData, isPremium, activeRole } =
    useAuth();
  // WL-GTM-7 follow-up: `activeRole !== 'advisor'` alone lets a Kanzlei that
  // switched into its own owner tenant see the "Kanzlei einladen" CTA
  // (`activeRole` reflects only the currently selected tenant, not whether
  // the user is a Steuerberater at all — see `useIsAdvisorContext.ts` module
  // header). Hook called unconditionally at component top-level.
  const isAdvisor = useIsAdvisorContext();

  // WL-F1 (Premium-Trigger 4 "Kanzlei-Übergabe"): der Export-Moment-Trigger darf
  // NUR erscheinen, wenn (a) der Nutzer selbst noch nicht Premium ist, (b) er
  // Loop-B-beta ist (kosmetisch, `isLoopBBeta`), und (c) die aktive Ansicht
  // WIRKLICH der eigene Tenant ist — `isPremium` aus `useAuth()` spiegelt IMMER
  // das eigene Konto, nie den gerade betrachteten `effectiveScopeUid`. Ein
  // Berater, der (via TenantSwitcher) in einen Mandanten-Tenant wechselt und dort
  // exportiert, zahlt selbst nicht — die Prüfung wäre sonst auf sein EIGENES
  // `isPremium` fehlgeleitet, nicht auf das des Mandanten (kein Server-Zugriffs-
  // schutz — rein kosmetisch, wie jedes Loop-B-FE-Gate).
  const [showPaywall, setShowPaywall] = useState(false);
  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;
  // Pricing-Repackaging §3.2/§3.2b: effektiver Owner-Tier (immer aus dem eigenen
  // `sellerData` — `useAuth()` spiegelt nie den betrachteten Mandanten). Der
  // Belege-Term gilt NUR im Owner-Kontext; im Mandanten-Modus (`!isOwnScope`)
  // trägt `canUseDatevBelege` KEINEN Owner-Tier-Term (StB nie aussperren).
  const ownerTier = resolveTier(sellerData);

  // WL-GTM-7 (b) "DATEV-Export-Erfolgsmoment": beta + owner + own scope, wie
  // die F1-Prüfung oben. Der Fetch selbst startet nur, wenn diese synchronen
  // Vorbedingungen bereits stehen (Kosten-Guard, mirrors WL-F1's isBeta-Gate
  // server-seitig).
  const isOwnerLoopBBeta =
    isLoopBBeta(currentUser?.uid, sellerData) &&
    isOwnScope &&
    activeRole !== 'advisor' &&
    !isAdvisor;
  const { canShowInviteCta } = useAdvisorInviteFunnelStatus(isOwnerLoopBBeta);
  const [showAdvisorInviteCard, setShowAdvisorInviteCard] = useState(false);

  const handleExportSuccess = (): void => {
    const f1Eligible =
      !isPremium &&
      isOwnScope &&
      isLoopBBeta(currentUser?.uid, sellerData) &&
      shouldShowKanzleiUebergabePaywall('export_moment');

    if (f1Eligible) {
      // F1 (Premium-Conversion) hat Vorrang bei diesem Export-Moment — die
      // GTM-7-Karte darf NICHT gleichzeitig stapeln (Kollisions-Auflösung,
      // s. AdvisorInviteExportSuccessCard-Modul-Kopf).
      setShowPaywall(true);
      return;
    }

    if (
      isOwnerLoopBBeta &&
      canShowInviteCta &&
      !isAdvisorInviteCtaDismissed('export_moment_card')
    ) {
      setShowAdvisorInviteCard(true);
    }
  };

  const [invoices, setInvoices] = useState<DatevInvoiceInput[]>([]);
  const [expenses, setExpenses] = useState<DatevExpenseInput[]>([]);
  // RK-8: beleglose Reise-Pauschalen (§3.5/§8 architecture-reisekosten.md). Fail-closed
  // gegated über `canUseTravelExpenses` — ohne Freigabe bleibt der Zustand `[]`, egal was
  // `loadMandantDatevSource` liefert (der Loader selbst kennt das Flag nicht, RK-6 lädt
  // travel_trips unconditional; die Sichtbarkeits-Gate liegt hier auf Seiten-Ebene).
  const [travel, setTravel] = useState<DatevTravelInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Auto-open: this is a dedicated export page — the export form is its whole
  // purpose, so open it on load instead of hiding it behind a redundant button.
  const [dialogOpen, setDialogOpen] = useState(true);

  // M7b: Belege-Paket (ZIP) — fail-closed Sichtbarkeit + Page-verwalteter Download-Zustand
  // (Netzwerk-Aufruf lebt hier, NICHT im Dialog — Dialog liefert nur den bereits gebauten
  // `DatevExportResult`/`DatevHeaderConfig` als Argumente, s. `onDownloadBelegePaket`).
  const [belegeDownloading, setBelegeDownloading] = useState(false);
  const [belegeError, setBelegeError] = useState<string | null>(null);
  const [belegeSkippedCount, setBelegeSkippedCount] = useState<number | null>(null);
  const canUseBelege = canUseDatevBelege(effectiveScopeUid, ownerTier, !isOwnScope);

  const isSmallBusiness = sellerData?.isSmallBusiness === true;

  const sellerDatevConfig = useMemo(
    () => ({
      skr: (sellerData?.datevSkr as SkrKontenrahmen | undefined) ?? 'SKR03',
      beraterNr: sellerData?.datevBeraterNr ?? null,
      mandantenNr: sellerData?.datevMandantenNr ?? null,
    }),
    [sellerData?.datevSkr, sellerData?.datevBeraterNr, sellerData?.datevMandantenNr],
  );

  /**
   * Persistiert DATEV-Konfiguration (Berater-/Mandanten-Nr. + SKR) ins Verkäuferprofil.
   * Best-effort: Fehler werden geloggt, aber der Export nicht unterbrochen.
   * Wird als Callback an den Dialog gereicht, damit der Dialog kein useAuth importiert.
   */
  const handlePersistDatevConfig = (cfg: {
    skr: SkrKontenrahmen;
    beraterNr: string;
    mandantenNr: string;
  }): void => {
    saveSellerData({
      datevSkr: cfg.skr,
      datevBeraterNr: cfg.beraterNr,
      datevMandantenNr: cfg.mandantenNr,
    }).catch((err) => {
      logger.error('DatevExportPage: persist DATEV config failed', err);
    });
  };

  /**
   * M7b-5c Option A: Zwei-Download-Fluss für das Belege-Paket (aus dem bereits vom Dialog
   * erzeugten `DatevExportResult`/`DatevHeaderConfig`; die Page dupliziert diesen Dialog-
   * lokalen Zustand bewusst nicht).
   *
   * 1. Nur Refs MIT erwarteter Datei (`hasFileExpectation`, P5) an die Function senden —
   *    das hält die Server-Skip-Liste kurz (kein `no_file_path` für belebslose Buchungen).
   * 2. Function-Call abwarten → ZIP-Download (in `downloadDatevBelegePaket` getriggert) +
   *    `skippedKeys` (server-autoritativ real fehlende Dateien).
   * 3. `finalKeys = availableKeys \ skippedKeys` → nur diese Buchungen tragen einen
   *    Beleglink; bei Header-Truncation (mehr Skips als gemeldete Keys) fail-closed auf
   *    KEINE Beleglinks (nie ein toter DATEV-Link, CSV bleibt gültig).
   * 4. EXTF-Buchungsstapel als getrennter CP1252-CSV-Download (FE-Artefakt).
   *
   * Fehler werden geloggt + als UI-Meldung gehalten, OHNE den unabhängigen M7-CSV-Export
   * zu beeinträchtigen (eigener Try/Catch, eigener Button, kein gemeinsamer Fehlerzustand).
   */
  const handleDownloadBelegePaket = async (
    result: DatevExportResult,
    header: DatevHeaderConfig,
  ): Promise<void> => {
    setBelegeError(null);
    setBelegeSkippedCount(null);
    setBelegeDownloading(true);
    try {
      // 1. Refs mit erwarteter Datei (P5). Nur diese gehen an die Function.
      const availableRefs = result.documentRefs.filter((ref) => ref.hasFileExpectation === true);
      const availableKeys = new Set(availableRefs.map((ref) => ref.documentKey));

      // 2. Function-Call → ZIP-Download + skippedKeys.
      const { skipped, skippedKeys } = await downloadDatevBelegePaket({
        fromISO: header.fromISO,
        toISO: header.toISO,
        header,
        refs: availableRefs.map((ref) => ({
          collection: ref.collection,
          docId: ref.docId,
          fileKind: ref.fileKind,
          guid: ref.guid,
          filenameInZip: ref.filenameInZip,
        })),
      });

      // 3. finalKeys = availableKeys \ skippedKeys. Truncation (Zähler > gemeldete Keys)
      //    → fail-closed keine Beleglinks (Header-Cap, s. Function).
      const truncated = skipped > skippedKeys.length;
      const skippedSet = new Set(skippedKeys);
      const finalKeys = truncated
        ? new Set<string>()
        : new Set(Array.from(availableKeys).filter((key) => !skippedSet.has(key)));

      // 4. EXTF-Buchungsstapel als getrennter CP1252-CSV-Download (Beleglinks nur für finalKeys).
      const csvLines = buildBelegeCsvLines(result, header, finalKeys);
      const csvBlob = buildDatevBlob(csvLines);
      downloadBlob(csvBlob, buildDatevFilename(header));

      setBelegeSkippedCount(skipped);
    } catch (err) {
      logger.error('DatevExportPage: Belege-Paket-Download fehlgeschlagen', err);
      setBelegeError(err instanceof Error ? err.message : String(err));
    } finally {
      setBelegeDownloading(false);
    }
  };

  useEffect(() => {
    // Gate-Kohärenz (WL-K7): gegen `effectiveScopeUid` prüfen — konsistent mit
    // `canUseDatevBelege`/`canUseInboundDatev`. Für den Owner identisch zu
    // `currentUser.uid`; nur im Advisor-Fall (fremder Tenant) weicht der Scope ab.
    if (!canUseDatevExport(effectiveScopeUid, ownerTier, !isOwnScope) || !currentUser) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        // Fail-closed: kein Teil-Export aus unvollständigen Daten — beide Quellen
        // müssen laden, sonst Fehlerzustand (kein unvollständiger Buchungsstapel).
        // Scope EXPLIZIT `effectiveScopeUid` (shared Loader, WL-K7).
        const { invoices: mappedInvoices, expenses: mappedExpenses, travel: mappedTravel } =
          await loadMandantDatevSource(effectiveScopeUid, isSmallBusiness);
        if (cancelled) return;
        setInvoices(mappedInvoices);
        setExpenses(mappedExpenses);
        // RK-8: Reise-Pauschalen nur bei Freigabe sichtbar machen (fail-closed) —
        // `mappedTravel` ist bereits auf settled/cancelled gefiltert (RK-6 Loader).
        // GA-Umzug §3: Reisekosten ist Ultra → `ownerTier` (nicht mehr `isPremium`).
        setTravel(canUseTravelExpenses(currentUser?.uid, ownerTier) ? mappedTravel : []);
      } catch (err) {
        if (!cancelled) {
          logger.error('DatevExportPage: load failed', err);
          setError(String(err instanceof Error ? err.message : err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser, effectiveScopeUid, isSmallBusiness, isPremium, ownerTier, isOwnScope]);

  // Fail-closed: ohne Beta-Allowlist/Flag-Freigabe nichts rendern (Gate-Kohärenz
  // WL-K7: `effectiveScopeUid` statt `currentUser.uid`). T4b (§3.2b-FIX): dritter
  // Arg `!isOwnScope` — im Mandanten-Modus (StB betrachtet fremden Tenant) gilt der
  // Advisor-Zweig (reines Flag), NICHT der Owner-Ultra-Term. So bleibt der Advisor-
  // Bypass in `canUseDatevBelege` (Z.132) erreichbar statt vorher totzulaufen.
  if (!canUseDatevExport(effectiveScopeUid, ownerTier, !isOwnScope)) {
    // PR-T6 (D7, §3.2): Upsell-Teaser statt Leerrendern — NUR für den Owner-
    // Zweig bei aktivem Flag. D-A4/Auflage 3: ein Advisor (`!isOwnScope`)
    // erreicht diesen Zweig als „nicht berechtigt" NUR, wenn `DATEV_EXPORT_FLAG`
    // aus ist (bei Flag an gewährt `canUseDatevExport` jedem Advisor Zugriff,
    // §3.2b) — dort bleibt es beim bisherigen `null` (kein Kaufanreiz für eine
    // Beta-Erprobung, UND explizit NIE ein Teaser/Paywall-Moment für einen StB,
    // T6-Auflage 3). Der bestehende Export-Erfolgsmoment-Paywall-Trigger
    // (`showPaywall`/`PaywallModal`, s. o.) bleibt unverändert und unabhängig
    // von diesem Vor-Render-Gate — kein zweiter, duplizierter Paywall-Mechanismus.
    if (DATEV_EXPORT_FLAG && isOwnScope) {
      return <DatevExportTeaser />;
    }
    return null;
  }

  return (
    <div className="min-h-screen bg-surface p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
            <IconCsv className="text-accent-ink" size={24} />
            {t('title')}
          </h1>
          <p className="text-muted text-sm mt-1">{t('subtitle')}</p>
        </div>

        {loading && (
          <div className="flex justify-center py-16 text-muted text-sm">{t('loading')}</div>
        )}

        {!loading && error && (
          <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-6 text-center">
            <IconError className="text-[var(--tone-danger-fg)] mx-auto mb-3" size={30} />
            <p className="text-[var(--tone-danger-fg)] font-medium">{t('error.title')}</p>
            <p className="text-[var(--tone-danger-fg)] text-xs mt-1 font-mono">{error}</p>
          </div>
        )}

        {!loading && !error && !dialogOpen && (
          <div className="rounded-xl border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.5)] p-6 text-center">
            <IconCsv className="text-accent-ink mx-auto mb-4" size={36} />
            <p className="text-secondary text-sm mb-5">{t('subtitle')}</p>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
            >
              <IconCsv />
              {t('title')}
            </button>
          </div>
        )}

        {dialogOpen && (
          <DatevExportDialog
            invoices={invoices}
            expenses={expenses}
            travel={travel}
            sellerDatevConfig={sellerDatevConfig}
            onClose={() => setDialogOpen(false)}
            onPersistDatevConfig={handlePersistDatevConfig}
            canUseBelege={canUseBelege}
            onDownloadBelegePaket={handleDownloadBelegePaket}
            belegeDownloading={belegeDownloading}
            belegeError={belegeError}
            belegeSkippedCount={belegeSkippedCount}
            onExportSuccess={handleExportSuccess}
            showAdvisorInviteFooterCta={isOwnerLoopBBeta && canShowInviteCta}
            onNavigateToExpense={() => navigate('/dashboard/ausgaben')}
          />
        )}

        {/* WL-GTM-7 (b): mutually exclusive with `showPaywall` above — see
            `handleExportSuccess` (Kollisions-Auflösung). */}
        {showAdvisorInviteCard && (
          <AdvisorInviteExportSuccessCard
            onDismiss={() => {
              dismissAdvisorInviteCta('export_moment_card');
              setShowAdvisorInviteCard(false);
            }}
          />
        )}

        {showPaywall && (
          <PaywallModal
            trigger="kanzlei_uebergabe"
            variant="export_moment"
            onClose={() => setShowPaywall(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DatevExportPage;
